import { create } from 'zustand';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { mergeProfiles, normalizeProfile, toSaved } from '../components/terminal/terminalProfiles';

/**
 * Terminal profiles + saved layouts, persisted per ACCOUNT on this device
 * (`terminal_config_load/save` → `account_dir/terminal/config.json`, see
 * src-tauri/src/terminal/config.rs), plus the session's CLI detection cache.
 *
 * File shape `{ v: 1, profiles, layouts, prefs, …unknown }`; `prefs` holds
 * `defaultProfileId` and `defaultCwd` (the Terminal's root folder — absent
 * in older files, meaning none; device-local like the rest). Unknown
 * top-level keys are kept on save (forward compatibility). No secrets live
 * here: profiles hold plain env the user typed, layouts hold ids and paths.
 */
const SAVE_DEBOUNCE_MS = 250;
let saveTimer = null;
let extraKeys = {};

const serialize = (s) => ({
  ...extraKeys,
  v: 1,
  profiles: s.profiles.map(toSaved),
  layouts: s.layouts,
  prefs: s.prefs,
});

export const useTerminalConfigStore = create((set, get) => ({
  loaded: false,
  profiles: mergeProfiles([]),
  layouts: [],
  prefs: { defaultProfileId: null },
  /** name -> { path, via } | null (not installed); absent = not asked yet */
  detection: {},
  detectionProblems: {}, // name → 'app' | 'folder' (card 6)
  detecting: false,

  load: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    let cfg = null;
    try {
      cfg = await invoke('terminal_config_load');
    } catch {
      cfg = null;
    }
    if (cfg && typeof cfg === 'object') {
      const { v, profiles, layouts, prefs, ...rest } = cfg;
      extraKeys = rest;
      set({
        profiles: mergeProfiles(profiles),
        layouts: Array.isArray(layouts) ? layouts.filter((l) => l && l.id && l.tree) : [],
        prefs: { defaultProfileId: null, ...(prefs && typeof prefs === 'object' ? prefs : {}) },
      });
    }
    set({ loaded: true });
  },

  /** Debounced write of the whole config. */
  save: () => {
    if (!isTauri()) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      invoke('terminal_config_save', { config: serialize(get()) }).catch((err) =>
        console.error('[terminal] config save failed', err)
      );
    }, SAVE_DEBOUNCE_MS);
  },

  /** Ask Rust where each profile's command is. Cached for the session. */
  detect: async (force = false) => {
    const { profiles, detection, detecting } = get();
    const names = [...new Set(profiles.map((p) => p.command).filter(Boolean))];
    const todo = force ? names : names.filter((n) => !(n in detection));
    if (!todo.length || detecting || !isTauri()) return;
    set({ detecting: true });
    try {
      const res = await invoke('terminal_detect_programs', { names: todo });
      const next = { ...get().detection };
      // Why a path cannot run ('app' / 'folder', card 6) — kept apart from
      // `detection`, where any truthy entry means "installed".
      const problems = { ...get().detectionProblems };
      (Array.isArray(res) ? res : []).forEach((r) => {
        next[r.name] = r.path ? { path: r.path, via: r.via } : null;
        if (r.problem) problems[r.name] = r.problem;
        else delete problems[r.name];
      });
      set({ detection: next, detectionProblems: problems });
    } catch {
      // leave unknown; the menu keeps showing the spinner-less "unknown" state
    } finally {
      set({ detecting: false });
    }
  },

  upsertProfile: (profile) => {
    const p = normalizeProfile(profile);
    set((s) => {
      const exists = s.profiles.some((x) => x.id === p.id);
      return { profiles: exists ? s.profiles.map((x) => (x.id === p.id ? { ...x, ...p, builtin: x.builtin, install: x.install } : x)) : [...s.profiles, p] };
    });
    get().save();
    if (!(p.command in get().detection)) get().detect();
  },

  deleteProfile: (id) => {
    set((s) => ({ profiles: s.profiles.filter((p) => p.builtin || p.id !== id) }));
    get().save();
  },

  /** The Terminal's default folder; '' (or blanks) removes it. */
  setDefaultCwd: (path) => {
    const value = typeof path === 'string' ? path : '';
    set((s) => {
      const prefs = { ...s.prefs };
      if (value.trim()) prefs.defaultCwd = value;
      else delete prefs.defaultCwd;
      return { prefs };
    });
    get().save();
  },

  /**
   * Hide / show a detected shell in the shell pickers (TerminalShellVisibilitySetting).
   * Stored by the shell's stable `key`; a stale key for an uninstalled shell is harmless.
   */
  setShellHidden: (key, hidden) => {
    if (!key) return;
    set((s) => {
      const current = Array.isArray(s.prefs?.hiddenShells) ? s.prefs.hiddenShells : [];
      const next = hidden ? [...new Set([...current, key])] : current.filter((k) => k !== key);
      const prefs = { ...s.prefs };
      if (next.length) prefs.hiddenShells = next;
      else delete prefs.hiddenShells;
      return { prefs };
    });
    get().save();
  },

  setProfileHidden: (id, hidden) => {
    set((s) => ({ profiles: s.profiles.map((p) => (p.id === id ? { ...p, hidden } : p)) }));
    get().save();
  },

  // ── layouts (phase 3 uses these) ─────────────────────────────────────────
  addLayout: (layout) => {
    set((s) => ({ layouts: [...s.layouts, layout] }));
    get().save();
  },
  renameLayout: (id, name) => {
    set((s) => ({ layouts: s.layouts.map((l) => (l.id === id ? { ...l, name, updatedAt: new Date().toISOString() } : l)) }));
    get().save();
  },
  /** Returns a function that restores the deleted layout at its old index. */
  deleteLayout: (id) => {
    const before = get().layouts;
    const idx = before.findIndex((l) => l.id === id);
    if (idx === -1) return () => {};
    const removed = before[idx];
    set({ layouts: before.filter((l) => l.id !== id) });
    get().save();
    return () => {
      set((s) => {
        if (s.layouts.some((l) => l.id === id)) return {};
        const next = [...s.layouts];
        next.splice(Math.min(idx, next.length), 0, removed);
        return { layouts: next };
      });
      get().save();
    };
  },
}));

/** Test-only: forget the file's unknown keys between tests. */
export const __resetTerminalConfigExtras = () => {
  extraKeys = {};
};
